import type { MentionEntity } from '@terra/shared-dto-chat-types';
import { type MessageSegment } from '@terra/shared-dto-chat-types';

export type MentionEntityWithOffset = {
  offset: number;
  entityId: string;
  entityType: MentionEntity['entityType'];
  label: string;
  extraData?: MentionEntity['extraData'];
};

export function buildMessageSegments(
  plainText: string,
  positionedMentions: MentionEntityWithOffset[],
): MessageSegment[] {
  if (positionedMentions.length === 0) return [];

  const segments: MessageSegment[] = [];
  let lastIndex = 0;
  const sorted = [...positionedMentions].sort((a, b) => a.offset - b.offset);

  for (const mention of sorted) {
    const textBefore = plainText.slice(lastIndex, mention.offset);

    if (textBefore) {
      segments.push({ type: 'text', value: textBefore });
    }

    segments.push({
      type: 'mention',
      entityId: mention.entityId,
      entityType: mention.entityType,
      label: mention.label,
    });
    lastIndex = mention.offset + 1 + mention.label.length;
  }

  const textAfter = plainText.slice(lastIndex);

  if (textAfter) {
    segments.push({ type: 'text', value: textAfter });
  }

  return segments;
}

type MentionDisplayFallback = {
  entityId: string;
  entityType: MentionEntity['entityType'];
  label: string;
};

function isValidMentionOffset({
  plainText,
  mention,
}: {
  plainText: string;
  mention: MentionEntityWithOffset;
}): boolean {
  if (mention.offset < 0 || mention.offset >= plainText.length) {
    return false;
  }

  const needle = `@${mention.label}`;

  return (
    plainText.slice(mention.offset, mention.offset + needle.length) === needle
  );
}

function filterValidPositionedMentions(
  plainText: string,
  positionedMentions: MentionEntityWithOffset[],
): MentionEntityWithOffset[] {
  return positionedMentions.filter((mention) =>
    isValidMentionOffset({ plainText, mention }),
  );
}

function getMentionEntityIdsFromSegments(
  segments: MessageSegment[],
): Set<string> {
  return new Set(
    segments
      .filter((segment) => segment.type === 'mention')
      .map((segment) => segment.entityId),
  );
}

function segmentsCoverMentionEntityIds({
  segments,
  entityIds,
}: {
  segments: MessageSegment[];
  entityIds: ReadonlySet<string>;
}): boolean {
  if (entityIds.size === 0) {
    return segments.some((segment) => segment.type === 'mention');
  }

  const foundEntityIds = getMentionEntityIdsFromSegments(segments);

  for (const entityId of entityIds) {
    if (!foundEntityIds.has(entityId)) {
      return false;
    }
  }

  return foundEntityIds.size > 0;
}

function resolveMentionOffsetsFromPlainText(
  plainText: string,
  mentions: MentionDisplayFallback[],
): MentionEntityWithOffset[] {
  const positioned: MentionEntityWithOffset[] = [];

  for (const mention of mentions) {
    const needle = `@${mention.label}`;
    const offset = plainText.indexOf(needle);

    if (offset === -1) {
      continue;
    }

    positioned.push({
      offset,
      entityId: mention.entityId,
      entityType: mention.entityType,
      label: mention.label,
    });
  }

  return positioned.sort((left, right) => left.offset - right.offset);
}

function buildChipFirstFallbackSegments({
  plainText,
  fallbackMentions,
}: {
  plainText: string;
  fallbackMentions: MentionDisplayFallback[];
}): MessageSegment[] {
  let cleanedText = plainText;

  for (const mention of fallbackMentions) {
    cleanedText = cleanedText.split(`@${mention.label}`).join('');
  }

  cleanedText = cleanedText.replace(/ {2,}/g, ' ').trim();

  const segments: MessageSegment[] = fallbackMentions.map((mention) => ({
    type: 'mention',
    entityId: mention.entityId,
    entityType: mention.entityType,
    label: mention.label,
  }));

  if (cleanedText) {
    segments.push({ type: 'text', value: cleanedText });
  }

  return segments;
}

export function buildMentionDisplaySegments({
  plainText,
  positionedMentions,
  fallbackMentions,
}: {
  plainText: string;
  positionedMentions: MentionEntityWithOffset[];
  fallbackMentions: MentionDisplayFallback[];
}): MessageSegment[] {
  const requiredEntityIds = new Set(
    fallbackMentions.map((mention) => mention.entityId),
  );

  const validPositionedMentions = filterValidPositionedMentions(
    plainText,
    positionedMentions,
  );
  const editorSegments = buildMessageSegments(
    plainText,
    validPositionedMentions,
  );

  if (
    editorSegments.length > 0 &&
    segmentsCoverMentionEntityIds({
      segments: editorSegments,
      entityIds: requiredEntityIds,
    })
  ) {
    return editorSegments;
  }

  const resolvedFromPlainText = buildMessageSegments(
    plainText,
    resolveMentionOffsetsFromPlainText(plainText, fallbackMentions),
  );

  if (
    resolvedFromPlainText.length > 0 &&
    segmentsCoverMentionEntityIds({
      segments: resolvedFromPlainText,
      entityIds: requiredEntityIds,
    })
  ) {
    return resolvedFromPlainText;
  }

  if (fallbackMentions.length === 0) {
    return editorSegments;
  }

  return buildChipFirstFallbackSegments({ plainText, fallbackMentions });
}
